import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Heart, Star, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useCurrency } from '../context/CurrencyContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ProductDetail = () => {
  const { productId } = useParams();
  const { user } = useAuth();
  const { formatPrice } = useCurrency();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState(null);
  const [inWishlist, setInWishlist] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    rating: 5,
    title: '',
    comment: ''
  });

  const fetchProductDetails = useCallback(async () => {
    try {
      const [productRes, reviewsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/products/${productId}`),
        axios.get(`${BACKEND_URL}/api/reviews/${productId}`),
        axios.get(`${BACKEND_URL}/api/reviews/${productId}/stats`)
      ]);

      setProduct(productRes.data);
      const imageMedia = (productRes.data.image_urls || []).map((url, index) => ({
        id: `image-${index}`,
        type: 'image',
        url
      }));
      const videoMedia = (productRes.data.video_urls || []).map((url, index) => ({
        id: `video-${index}`,
        type: 'video',
        url
      }));
      const galleryMedia = [...imageMedia, ...videoMedia];
      setSelectedMedia(galleryMedia[0] || null);
      setReviews(reviewsRes.data);
      setReviewStats(statsRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/products');
    }
  }, [productId, navigate]);

  useEffect(() => {
    fetchProductDetails();
  }, [fetchProductDetails]);

  useEffect(() => {
    const fetchWishlistStatus = async () => {
      if (!user) {
        setInWishlist(false);
        return;
      }

      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/wishlist`, {
          withCredentials: true
        });
        setInWishlist((data.items || []).includes(productId));
      } catch (error) {
        console.error('Failed to fetch wishlist status:', error);
      }
    };

    fetchWishlistStatus();
  }, [user, productId]);

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please log in to add items to your cart');
      navigate('/login');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/cart`, { product_id: productId, quantity: 1 }, { withCredentials: true });
      toast.success('Added to cart');
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Your session expired. Please log in again.');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
    }
  };

  const handleWishlistToggle = async () => {
    if (!user) {
      toast.error('Please log in to manage your wishlist');
      navigate('/login');
      return;
    }

    try {
      if (inWishlist) {
        await axios.delete(`${BACKEND_URL}/api/wishlist/${productId}`, {
          withCredentials: true
        });
        setInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/wishlist`,
          { product_id: productId },
          { withCredentials: true }
        );
        setInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Your session expired. Please log in again.');
        navigate('/login');
        return;
      }
      toast.error(error.response?.data?.detail || 'Failed to update wishlist');
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      await axios.post(
        `${BACKEND_URL}/api/reviews`,
        {
          product_id: productId,
          rating: reviewForm.rating,
          title: reviewForm.title,
          comment: reviewForm.comment
        },
        { withCredentials: true }
      );
      toast.success('Review submitted successfully');
      setShowReviewForm(false);
      setReviewForm({ rating: 5, title: '', comment: '' });
      fetchProductDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    }
  };

  if (loading) {
    return (
      <div className="heritage-page min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c79a35]"></div>
      </div>
    );
  }

  const galleryMedia = [
    ...(product.image_urls || []).map((url, index) => ({
      id: `image-${index}`,
      type: 'image',
      url
    })),
    ...(product.video_urls || []).map((url, index) => ({
      id: `video-${index}`,
      type: 'video',
      url
    }))
  ];
  const activeMedia = selectedMedia || galleryMedia[0] || null;

  const handleMediaSwipe = (direction) => {
    if (galleryMedia.length <= 1 || !activeMedia) {
      return;
    }

    const currentIndex = galleryMedia.findIndex((media) => media.id === activeMedia.id);
    const safeIndex = currentIndex >= 0 ? currentIndex : 0;
    const nextIndex =
      direction === 'left'
        ? (safeIndex + 1) % galleryMedia.length
        : (safeIndex - 1 + galleryMedia.length) % galleryMedia.length;

    setSelectedMedia(galleryMedia[nextIndex]);
  };

  const handleTouchStart = (event) => {
    setTouchStartX(event.touches[0]?.clientX ?? null);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null) {
      return;
    }

    const touchEndX = event.changedTouches[0]?.clientX ?? touchStartX;
    const swipeDistance = touchStartX - touchEndX;

    if (Math.abs(swipeDistance) > 40) {
      handleMediaSwipe(swipeDistance > 0 ? 'left' : 'right');
    }

    setTouchStartX(null);
  };

  return (
    <div className="heritage-page min-h-screen flex flex-col">
      <Header />

      <main className="heritage-shell flex-1">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div className="space-y-4">
            <div
              className="heritage-card aspect-square overflow-hidden rounded-sm"
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              {activeMedia?.type === 'video' ? (
                <video
                  src={activeMedia.url}
                  autoPlay
                  loop
                  muted
                  playsInline
                  controls
                  className="w-full h-full object-cover bg-black"
                />
              ) : (
                <img
                  src={activeMedia?.url || product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              )}
            </div>

            {galleryMedia.length > 1 && (
              <div className="sm:hidden flex items-center justify-center rounded-sm border border-[#dcc9ad] bg-[rgba(255,251,243,0.8)] px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-[#8e755d]">
                Swipe photos and videos
              </div>
            )}

            {galleryMedia.length > 1 && (
              <div className="grid grid-cols-4 gap-3">
                {galleryMedia.map((media) => (
                  <button
                    key={media.id}
                    type="button"
                    onClick={() => setSelectedMedia(media)}
                    className={`aspect-square overflow-hidden rounded-sm border transition ${
                      activeMedia?.id === media.id
                        ? 'border-[#c79a35]'
                        : 'border-[#E8DFE0]'
                    }`}
                  >
                    {media.type === 'video' ? (
                      <video
                        src={media.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        className="w-full h-full object-cover bg-black"
                      />
                    ) : (
                      <img
                        src={media.url}
                        alt={`${product.name} preview`}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <p className="heritage-kicker mb-2">
                {product.category}
              </p>
              <h1 className="heritage-title mb-4 text-5xl">
                {product.name}
              </h1>

              {reviewStats && reviewStats.total_reviews > 0 && (
                <div className="flex items-center space-x-4 mb-4">
                  <div className="flex items-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`h-5 w-5 ${
                          star <= Math.floor(reviewStats.average_rating)
                            ? 'fill-current text-amber-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-lg font-medium">{reviewStats.average_rating}</span>
                  <span className="text-[#6a5448]">({reviewStats.total_reviews} reviews)</span>
                </div>
              )}

              <p className="heritage-title text-4xl">
                {formatPrice(product.price)}
              </p>
            </div>

            <div className="heritage-divider border-t pt-6">
              <p className="text-base leading-8 text-[#4d3a31]">
                {product.description}
              </p>
            </div>

            <div className="flex gap-4">
              <Button
                onClick={handleAddToCart}
                className="heritage-button flex-1 rounded-none text-lg py-6"
                data-testid="add-to-cart-detail"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Add to Cart
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleWishlistToggle}
                className="rounded-sm px-5 py-6 border-[#dcc9ad] hover:bg-[#f5ebda]"
                data-testid="wishlist-toggle-detail"
              >
                <Heart
                  className={`h-5 w-5 ${inWishlist ? 'fill-current' : ''}`}
                  style={{ color: inWishlist ? 'var(--primary)' : '#6B7280' }}
                />
              </Button>
            </div>

            <div className="heritage-card rounded-sm p-4">
              <p className="text-sm text-[#6a5448]">
                <strong>Stock:</strong> {product.stock > 0 ? `${product.stock} available` : 'Out of stock'}
              </p>
            </div>
          </div>
        </div>

        <div className="heritage-divider border-t pt-12">
          <div className="flex justify-between items-center mb-8">
            <h2 className="heritage-title text-4xl">
              Customer Reviews
            </h2>
            {user && (
              <Button
                onClick={() => setShowReviewForm(!showReviewForm)}
                variant="outline"
                className="border-[#dcc9ad] bg-[rgba(255,250,242,0.88)] hover:bg-[#f3e7d2]"
                data-testid="write-review-btn"
              >
                Write a Review
              </Button>
            )}
          </div>

          {showReviewForm && (
            <form onSubmit={handleSubmitReview} className="heritage-card rounded-sm p-6 mb-8" data-testid="review-form">
              <div className="space-y-4">
                <div>
                  <Label>Rating</Label>
                  <div className="flex items-center space-x-2 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        className="focus:outline-none"
                      >
                        <Star
                          className={`h-8 w-8 cursor-pointer transition-colors ${
                            star <= reviewForm.rating
                              ? 'fill-current text-amber-400'
                              : 'text-gray-300 hover:text-amber-200'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="review-title">Title (Optional)</Label>
                  <Input
                    id="review-title"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                    placeholder="Great product!"
                    className="heritage-input mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="review-comment">Review</Label>
                  <Textarea
                    id="review-comment"
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    required
                    rows={4}
                    placeholder="Share your experience with this product..."
                    className="heritage-input mt-2"
                  />
                </div>

                <div className="flex gap-4">
                  <Button type="submit" className="heritage-button rounded-none">
                    Submit Review
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowReviewForm(false)}
                    className="rounded-sm"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </form>
          )}

          <div className="space-y-6">
            {reviews.length === 0 ? (
              <div className="heritage-card text-center rounded-sm py-12">
                <p className="text-[#6a5448]">No reviews yet. Be the first to review this product!</p>
              </div>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="heritage-card rounded-sm p-6" data-testid={`review-${review.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center space-x-3 mb-2">
                        <p className="font-medium">{review.user_name}</p>
                        <div className="flex items-center">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star
                              key={star}
                              className={`h-4 w-4 ${
                                star <= review.rating
                                  ? 'fill-current text-amber-400'
                                  : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                      </div>
                      {review.title && (
                        <h4 className="font-medium text-[#3f2b23] mb-2">{review.title}</h4>
                      )}
                    </div>
                    <span className="text-sm text-[#7b6457]">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-[#4d3a31] leading-relaxed">{review.comment}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductDetail;
